"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useState } from "react";
import { cn } from "@/lib/utils";
import { randomFloatBetween } from "@llmgateway/shared/random";
import type { RefObject } from "react";
export interface AnimatedBeamProps {
  className?: string;
  containerRef: RefObject<HTMLElement | null>; // Container ref
  fromRef: RefObject<HTMLElement | null>;
  toRef: RefObject<HTMLElement | null>;
  curvature?: number;
  reverse?: boolean;
  pathColor?: string;
  pathWidth?: number;
  pathOpacity?: number;
  gradientStartColor?: string;
  gradientStopColor?: string;
  delay?: number;
  duration?: number;
  startXOffset?: number;
  startYOffset?: number;
  endXOffset?: number;
  endYOffset?: number;
}
export const AnimatedBeam: React.FC<AnimatedBeamProps> = t0 => {
  const $ = _c(52);
  const {
    className,
    containerRef,
    fromRef,
    toRef,
    curvature: t1,
    reverse: t2,
    duration,
    delay: t3,
    pathColor: t4,
    pathWidth: t5,
    pathOpacity: t6,
    gradientStartColor: t7,
    gradientStopColor: t8,
    startXOffset: t9,
    startYOffset: t10,
    endXOffset: t11,
    endYOffset: t12
  } = t0;
  const curvature = t1 === undefined ? 0 : t1;
  const reverse = t2 === undefined ? false : t2;
  const delay = t3 === undefined ? 0 : t3;
  const pathColor = t4 === undefined ? "gray" : t4;
  const pathWidth = t5 === undefined ? 2 : t5;
  const pathOpacity = t6 === undefined ? 0.2 : t6;
  const gradientStartColor = t7 === undefined ? "#ffaa40" : t7;
  const gradientStopColor = t8 === undefined ? "#9c40ff" : t8;
  const startXOffset = t9 === undefined ? 0 : t9;
  const startYOffset = t10 === undefined ? 0 : t10;
  const endXOffset = t11 === undefined ? 0 : t11;
  const endYOffset = t12 === undefined ? 0 : t12;
  const id = useId();
  const [pathD, setPathD] = useState("");
  let t13;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      width: 0,
      height: 0
    };
    $[0] = t13;
  } else {
    t13 = $[0];
  }
  const [svgDimensions, setSvgDimensions] = useState(t13);
  let t14;
  if ($[1] !== duration) {
    t14 = () => duration ?? randomFloatBetween(4, 7);
    $[1] = duration;
    $[2] = t14;
  } else {
    t14 = $[2];
  }
  const [sweepDuration] = useState(t14);
  let t15;
  if ($[3] !== reverse) {
    t15 = reverse ? {
      x1: "90%;-10%",
      x2: "100%;0%"
    } : {
      x1: "10%;110%",
      x2: "0%;100%"
    };
    $[3] = reverse;
    $[4] = t15;
  } else {
    t15 = $[4];
  }
  const gradientCoordinates = t15;
  let t16;
  let t17;
  if ($[5] !== containerRef || $[6] !== curvature || $[7] !== endXOffset || $[8] !== endYOffset || $[9] !== fromRef || $[10] !== startXOffset || $[11] !== startYOffset || $[12] !== toRef) {
    t16 = () => {
      const updatePath = () => {
        if (containerRef.current && fromRef.current && toRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const rectA = fromRef.current.getBoundingClientRect();
          const rectB = toRef.current.getBoundingClientRect();
          const svgWidth = containerRect.width;
          const svgHeight = containerRect.height;
          setSvgDimensions({
            width: svgWidth,
            height: svgHeight
          });
          const startX = rectA.left - containerRect.left + rectA.width / 2 + startXOffset;
          const startY = rectA.top - containerRect.top + rectA.height / 2 + startYOffset;
          const endX = rectB.left - containerRect.left + rectB.width / 2 + endXOffset;
          const endY = rectB.top - containerRect.top + rectB.height / 2 + endYOffset;
          const controlY = startY - curvature;
          const d = `M ${startX},${startY} Q ${(startX + endX) / 2},${controlY} ${endX},${endY}`;
          setPathD(d);
        }
      };
      const resizeObserver = new ResizeObserver(entries => {
        for (const _entry of entries) {
          updatePath();
        }
      });
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      return () => {
        resizeObserver.disconnect();
      };
    };
    t17 = [containerRef, fromRef, toRef, curvature, startXOffset, startYOffset, endXOffset, endYOffset];
    $[5] = containerRef;
    $[6] = curvature;
    $[7] = endXOffset;
    $[8] = endYOffset;
    $[9] = fromRef;
    $[10] = startXOffset;
    $[11] = startYOffset;
    $[12] = toRef;
    $[13] = t16;
    $[14] = t17;
  } else {
    t16 = $[13];
    t17 = $[14];
  }
  useEffect(t16, t17);
  const t18 = svgDimensions.width;
  const t19 = svgDimensions.height;
  let t20;
  if ($[15] !== className) {
    t20 = cn("pointer-events-none absolute left-0 top-0 transform-gpu stroke-2", className);
    $[15] = className;
    $[16] = t20;
  } else {
    t20 = $[16];
  }
  const t21 = `0 0 ${svgDimensions.width} ${svgDimensions.height}`;
  let t22;
  if ($[17] !== pathColor || $[18] !== pathD || $[19] !== pathOpacity || $[20] !== pathWidth) {
    t22 = <path d={pathD} stroke={pathColor} strokeWidth={pathWidth} strokeOpacity={pathOpacity} strokeLinecap="round" />;
    $[17] = pathColor;
    $[18] = pathD;
    $[19] = pathOpacity;
    $[20] = pathWidth;
    $[21] = t22;
  } else {
    t22 = $[21];
  }
  const t23 = `url(#${id})`;
  let t24;
  if ($[22] !== pathD || $[23] !== pathWidth || $[24] !== t23) {
    t24 = <path d={pathD} strokeWidth={pathWidth} stroke={t23} strokeOpacity="1" strokeLinecap="round" />;
    $[22] = pathD;
    $[23] = pathWidth;
    $[24] = t23;
    $[25] = t24;
  } else {
    t24 = $[25];
  }
  let t25;
  if ($[26] !== delay || $[27] !== gradientCoordinates || $[28] !== pathD || $[29] !== sweepDuration) {
    t25 = pathD && (["x1", "x2"] as const).map(attribute => <animate key={attribute} attributeName={attribute} values={gradientCoordinates[attribute]} keyTimes="0;1" calcMode="spline" keySplines="0.16 1 0.3 1" begin={`${delay}s`} dur={`${sweepDuration}s`} repeatCount="indefinite" />);
    $[26] = delay;
    $[27] = gradientCoordinates;
    $[28] = pathD;
    $[29] = sweepDuration;
    $[30] = t25;
  } else {
    t25 = $[30];
  }
  let t26;
  let t27;
  if ($[31] !== gradientStartColor) {
    t26 = <stop stopColor={gradientStartColor} stopOpacity="0" />;
    t27 = <stop stopColor={gradientStartColor} />;
    $[31] = gradientStartColor;
    $[32] = t26;
    $[33] = t27;
  } else {
    t26 = $[32];
    t27 = $[33];
  }
  let t28;
  let t29;
  if ($[34] !== gradientStopColor) {
    t28 = <stop offset="32.5%" stopColor={gradientStopColor} />;
    t29 = <stop offset="100%" stopColor={gradientStopColor} stopOpacity="0" />;
    $[34] = gradientStopColor;
    $[35] = t28;
    $[36] = t29;
  } else {
    t28 = $[35];
    t29 = $[36];
  }
  let t30;
  if ($[37] !== id || $[38] !== t25 || $[39] !== t26 || $[40] !== t27 || $[41] !== t28 || $[42] !== t29) {
    t30 = <defs><linearGradient className="transform-gpu" id={id} gradientUnits="userSpaceOnUse" x1="0%" x2="0%" y1="0%" y2="0%">{t25}{t26}{t27}{t28}{t29}</linearGradient></defs>;
    $[37] = id;
    $[38] = t25;
    $[39] = t26;
    $[40] = t27;
    $[41] = t28;
    $[42] = t29;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== svgDimensions.height || $[45] !== svgDimensions.width || $[46] !== t20 || $[47] !== t21 || $[48] !== t22 || $[49] !== t24 || $[50] !== t30) {
    t31 = <svg fill="none" width={t18} height={t19} xmlns="http://www.w3.org/2000/svg" className={t20} viewBox={t21}>{t22}{t24}{t30}</svg>;
    $[44] = svgDimensions.height;
    $[45] = svgDimensions.width;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
    $[49] = t24;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  return t31;
};